<script setup lang="ts">
import useSwr from "@/core/composables/swr/useSwr";
import {computed} from "vue";
import {consumerProductApi} from "@/container";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import DrugSelectionProductRow from "@/drug-interaction/components/drug-selection/raw/DrugSelectionProductRow.vue";
import {ProductSummary} from "@/product/interfaces/ProductSummary";
import {LegacyProduct} from "@/core/interfaces/Product";
import {cacheKey} from "@/lib/cache";
import {SourceType} from "@/consumer/interfaces";

const props = defineProps<{
    consumerKey: string;
    selectedProducts: ProductSummary[];
    searchBarProducts: LegacyProduct[];
    prescriptors?: number[];
}>();

defineEmits<{
    (e: 'select', product: ProductSummary, source: SourceType): void;
}>();

const key = computed(() => cacheKey(`consumers/${props.consumerKey}/sales-products`, {
    range: '1 year',
    prescriptors: props.prescriptors
}));

const {data, isLoading} = useSwr(
    key,
    () => consumerProductApi().salesIndex(props.consumerKey, {
        range: '1 year',
        doctors: props.prescriptors?.length ? props.prescriptors : undefined
    })
);

/**
 * The products to display in the list.
 */
const displayedProducts = computed(() => {
    const salesProducts = (data.value ?? []).map(sale => sale.product);
    const productsFromSales = salesProducts;

    if (props.prescriptors?.length) {
        return productsFromSales;
    }

    const productsFromConsumer = (props.selectedProducts ?? []).filter(product => {
        return !salesProducts.some(sale => sale.id === product.id);
    });

    const productsFromSearch = props.searchBarProducts
        .filter(product => {
            const isInSales = salesProducts.some(sale => sale.id === product.id);
            const isInConsumer = (props.selectedProducts ?? []).some(cp => cp.id === product.id);
            return !isInSales && !isInConsumer;
        })
        .map(product => ({
            id: product.id,
            name: product.name,
            cip: product.code,
            id_7: product.cip7
        }));

    return [
        ...productsFromSales,
        ...productsFromConsumer,
        ...productsFromSearch
    ];
});

/**
 * Check if a product is selected.
 *
 * @param product
 */
const isSelected = (product: ProductSummary) => {
    return props.selectedProducts.some(p => p.id === product.id);
};

/**
 * Get the history of a product.
 *
 * @param product
 */
const historyForProduct = (product: ProductSummary) => {
    return data.value?.find(sale => sale.product.id === product.id)?.history;
};

/**
 * Determine if a product has at least one prescription in its history.
 */
const hasPrescription = (product: ProductSummary) => {
    const history = historyForProduct(product);

    return history?.some(sale => sale.prescription);
};
</script>

<template>
  <div>
    <div v-if="isLoading">
      <BaseFlexSpinner />
    </div>
    <div v-else>
      <div v-if="!displayedProducts.length">
        <p class="text-center">
          Aucun produit trouvé.
        </p>
      </div>
      <div class="space-y-8">
        <DrugSelectionProductRow
          v-for="product in displayedProducts"
          :key="product?.id"
          :product="product"
          :selected="isSelected(product)"
          :history="historyForProduct(product)"
          @select="$emit(
            'select',
            product,
            hasPrescription(product) ? SourceType.PRESCRIPTION : SourceType.SELF_MEDICATION
          )"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
